/**
 * Notification Scheduler — WHEN a notification fires.
 *
 * The Background Scheduler's screen, for the other kind of schedule: one table,
 * one dialog, the enable switch in the row rather than behind an icon. They read
 * from the same collection and the same poller, so they should not be two
 * different experiences of one idea — a person who has used either one knows
 * this one.
 *
 * What differs is only what a notification schedule needs: the target is a
 * notification rather than a job, there is an EVENT type (fires when something
 * is announced instead of at a time), and `parameters` carry the values the
 * templates render — a clock-driven fire has no payload, so the schedule is the
 * only place they can come from.
 */
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { confirmDialog, ConfirmDialog } from 'primereact/confirmdialog';
import { InputSwitch } from 'sharedUi/InputSwitch';
import { Dropdown } from 'sharedUi/Dropdown';
import { Button } from 'sharedUi/Button';
import { Tag } from 'sharedUi/Tag';
import { formatTimestamp } from 'sharedUi/dateUtils';
import SaasDataTable, { type SaasColumn } from '../../components/common/saas-data-table';
import { ListCount } from '../../components/common/list-count';
import PermissionGuard from '../../components/guards/PermissionGuard';
import PageHeader from '../../components/common/page-header';
import { useToastApi } from '../../hooks/use-toast-api';
import { useCanCreate, useCanDelete, useCanUpdate } from '../../hooks/usePermissions';
import { useSecureNavigation } from '../../hooks/useSecureNavigation';
import { JOB_NAMES } from '../../constants/jobNames';
import { schedulesService, type Schedule } from '../../services/schedules.service';
import { ProjectCell } from '../../components/common/project-cell';
import { useAccessibleProjects, projectOptions } from '../../hooks/useProjectScope';
import { totalOf, SOONEST_NEXT_RUN, sortParams, type SortState } from '../../services/notification-engine.types';
import { notificationMastersService } from '../../services/notification-masters.service';
import { notificationTypesService } from '../../services/notification-types.service';
import { describeTiming } from '../../utils/schedule-timing';

const typeSeverity = (t?: string) =>
  t === 'CRON' ? 'info' : t === 'INTERVAL' ? 'warning' : t === 'EVENT' ? 'success' : 'secondary';

const NotificationSchedulesTab = ({ embedded = false }: { embedded?: boolean }) => {
  const qc = useQueryClient();
  const { toast, ok, fail } = useToastApi();
  const { navigateToView, navigateToEdit, navigateToCreate } = useSecureNavigation({ entity: 'schedule', basePath: '/schedules' });
  const canCreate = useCanCreate(JOB_NAMES.SCHEDULE_MASTER);
  const canUpdate = useCanUpdate(JOB_NAMES.SCHEDULE_MASTER);
  const canDelete = useCanDelete(JOB_NAMES.SCHEDULE_MASTER);

  const [first, setFirst] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  // Server-side, soonest next run first until the viewer picks a column —
  // "what fires next?" is the question a scheduler answers.
  const [sort, setSort] = useState<SortState>(SOONEST_NEXT_RUN);
  const [appFilter, setAppFilter] = useState('');
  // The COLUMN always shows; the filter beside it needs an actual choice.
  const { projects, canScopeByProject } = useAccessibleProjects();
  // Only notification schedules. The collection also holds the background jobs',
  // and a scheduler screen that mixes them makes "what is scheduled here?"
  // unanswerable.
  // A schedule's appId is INHERITED from its target, never set here — the service
  // refuses to trust a caller's value so an app-scoped notification cannot be
  // widened tenant-wide by a schedule that omitted it. So this is a filter over
  // that inherited value, not an editable field, and tenant-wide schedules stay
  // in the list whichever project is picked because they apply to all of them.
  const schedulesQ = useQuery({
    queryKey: ['engine-schedules', first, pageSize, sort, appFilter],
    queryFn: () => schedulesService.list({
      masterType: 'NOTIFICATION', skip: first, limit: pageSize, ...sortParams(sort),
      appId: appFilter || undefined,
    }),
  });
  const rows = schedulesQ.data?.data ?? [];
  const total = schedulesQ.data ? totalOf(schedulesQ.data) : rows.length;

  // The notifications a schedule can point at, named by their type's code —
  // a master has no name of its own.
  const mastersQ = useQuery({
    queryKey: ['notification-masters', 'for-schedules'],
    queryFn: () => notificationMastersService.list({ skip: 0, limit: 200 }),
    staleTime: 60_000,
  });
  const typesQ = useQuery({
    queryKey: ['notification-types', 'for-schedules'],
    queryFn: () => notificationTypesService.list({ skip: 0, limit: 200 }),
    staleTime: 60_000,
  });

  const masterOptions = useMemo(() => {
    const byId = new Map((typesQ.data?.data ?? []).map((t) => [String(t._id), t]));
    return (mastersQ.data?.data ?? []).map((m) => ({
      value: String(m._id),
      label: byId.get(String(m.notificationTypeId))?.notificationCode || String(m._id),
    }));
  }, [mastersQ.data, typesQ.data]);
  const masterLabel = (id?: string | null) =>
    masterOptions.find((o) => o.value === String(id))?.label || '—';

  const invalidate = () => qc.invalidateQueries({ queryKey: ['engine-schedules'] });

  const toggleM = useMutation({
    mutationFn: ({ id, on }: { id: string; on: boolean }) => schedulesService.toggleStatus(id, on),
    onSuccess: (_d, v) => {
      ok(v.on ? 'Enabled — the next run is computed from now' : 'Disabled — history is kept');
      invalidate();
    },
    onError: (e) => fail(e),
  });

  const deleteM = useMutation({
    mutationFn: (id: string) => schedulesService.softDelete(id),
    onSuccess: () => { ok('Schedule deleted'); invalidate(); },
    onError: (e) => fail(e),
  });

  const columns: SaasColumn<Schedule>[] = useMemo(() => [
    {
      field: 'masterId', header: 'Notification', hideable: false, style: { minWidth: 190 },
      body: (s) => <span className="font-mono text-xs">{masterLabel(s.masterId)}</span>,
    },
    { field: 'scheduleCode', header: 'Code', style: { minWidth: 140 },
      body: (s) => <span className="font-mono text-xs">{s.scheduleCode}</span> },
    {
      field: 'scheduleType', header: 'Type', style: { width: 110 },
      body: (s) => <Tag value={s.scheduleType} severity={typeSeverity(s.scheduleType)} />,
    },
    {
      // The timing itself, not just its kind: "CRON" does not say when this
      // fires, and that is the question the screen exists to answer.
      field: 'when', header: 'When', style: { minWidth: 150 },
      body: (s) => <span className="font-mono text-xs">{describeTiming(s)}</span>,
    },
    {
      field: 'nextRunAt', header: 'Next run', sortable: true, style: { minWidth: 170 },
      body: (s) => (s.scheduleType === 'EVENT'
        ? <span className="text-xs text-gray-400">on the event</span>
        : s.nextRunAt
          ? <span className="text-xs">{formatTimestamp(s.nextRunAt)}</span>
          // The poller selects on `nextRunAt <= now`, so a null one is never
          // picked up. Enabled with no next run reads as healthy and never fires.
          : <span className="text-xs text-amber-600 dark:text-amber-400"
                  title="The poller selects on nextRunAt, so this will never fire. A ONCE time that has passed clears it — set a future time.">
              <i className="pi pi-exclamation-triangle mr-1" style={{ fontSize: '0.7rem' }} />no next run
            </span>),
    },
    {
      field: 'lastRunAt', header: 'Last run', style: { minWidth: 165 },
      body: (s) => <span className="text-xs text-gray-500">{s.lastRunAt ? formatTimestamp(s.lastRunAt) : 'never'}</span>,
    },
    // Inherited from the schedule's target — see the query above.
    {
      field: 'appId', header: 'Project', style: { minWidth: 150 },
      body: (s: Schedule) => <ProjectCell appId={s.appId} projects={projects} />,
    },
    {
      /* The switch lives in the row rather than behind an icon: enabling and
         pausing is the routine act here, so it reads as state you flip. */
      field: 'isScheduleEnabled', header: 'Enabled', align: 'center', style: { width: 130 },
      body: (s) => {
        const completed = !s.isScheduleEnabled && s.scheduleType === 'ONCE' && !!s.lastRunAt;
        const busy = toggleM.isPending && toggleM.variables?.id === s._id;
        return (
          <div className="flex items-center justify-center gap-2"
               title={completed
                 ? 'This one-off already ran. Edit it to set a new time and re-enable together.'
                 : s.isScheduleEnabled
                   ? 'Pause — reversible, and the history is kept.'
                   : 'Enable — the next run is computed from now.'}>
            <InputSwitch checked={!!s.isScheduleEnabled}
              disabled={!canUpdate || completed || busy}
              onChange={(e) => toggleM.mutate({ id: s._id, on: !!e.value })} />
            {completed && <Tag value="Completed" severity="info" />}
            {!completed && s.isScheduleEnabled && !s.nextRunAt && s.scheduleType !== 'EVENT'
              && <Tag value="Won't fire" severity="warning" />}
          </div>
        );
      },
    },
    {
      field: 'actions', header: 'Actions', hideable: false, align: 'center', style: { width: 130 },
      body: (s) => (
        <div className="flex items-center justify-center gap-1">
          <Button aria-label="View" icon="pi pi-eye" text rounded tooltip="View schedule"
                  onClick={() => navigateToView(s._id)} />
          {canUpdate && <Button aria-label="Edit" icon="pi pi-pencil" text rounded tooltip="Edit schedule" onClick={() => navigateToEdit(s._id)} />}
          {canDelete && (
            <Button aria-label="Delete" icon="pi pi-trash" text rounded severity="danger" tooltip="Delete schedule"
              onClick={() => confirmDialog({
                message: `Delete schedule "${s.scheduleCode}"? Pausing it is reversible; deleting is not.`,
                header: 'Delete schedule', icon: 'pi pi-exclamation-triangle',
                acceptClassName: 'p-button-danger',
                accept: () => deleteM.mutate(s._id),
              })} />
          )}
        </div>
      ),
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [canUpdate, canDelete, toggleM.isPending, toggleM.variables?.id, masterOptions, projects]);

  return (
    <PermissionGuard jobName={JOB_NAMES.SCHEDULE_MASTER}>
      {toast}
      <ConfirmDialog />
      <div className={embedded ? '' : 'p-3'} data-testid="SCR-NotificationSchedules">
        {!embedded && (
          <PageHeader icon="pi pi-calendar" title="Notification Scheduler"
                      subtitle="When each notification fires" testId="NotificationSchedules.Header" />
        )}

        <SaasDataTable
          tableId="notification-schedules"
          columns={columns}
          data={rows}
          loading={schedulesQ.isFetching}
          dataKey="_id"
          emptyMessage="No notification schedules yet."
          first={first}
          rows={pageSize}
          totalRecords={total}
          onPage={(e: { first: number; rows: number }) => { setFirst(e.first); setPageSize(e.rows); }}
          rowsPerPageOptions={[10, 25, 50]}
          sortField={sort.field || undefined}
          sortOrder={sort.order}
          onSort={(e: { sortField: string; sortOrder: 1 | -1 | null }) => {
            // Server-side: the query key carries the sort, so this refetches from page one.
            setSort({ field: e.sortField || null, order: e.sortOrder || null });
            setFirst(0);
          }}
          onFilter={() => { /* the list is small and server-paged */ }}
          testId="NotificationSchedules"
          toolbarStart={canScopeByProject ? (
            <Dropdown
              value={appFilter}
              options={projectOptions(projects)}
              optionLabel="label" optionValue="value" placeholder="All Projects"
              showClear={!!appFilter} style={{ minWidth: 170 }}
              onChange={(e: { value: string }) => { setAppFilter(e.value || ''); setFirst(0); }}
              data-testid="NotificationSchedules.ProjectFilter"
            />
          ) : undefined}
          toolbarEnd={
            <>
              <ListCount total={total} onRefresh={() => schedulesQ.refetch()}
                loading={schedulesQ.isFetching} testId="NotificationSchedules.Refresh" />
              {canCreate && masterOptions.length > 0 && (
                <Button label="Add Schedule" icon="pi pi-plus" onClick={() => navigateToCreate()} />
              )}
            </>
          }
        />
      </div>

      {/* Creating and editing happen on the schedule form page (/schedules/create, /schedules/:id/edit), which the Add and Edit controls navigate to. An inline dialog used to do it here; its state was removed in an earlier change while the markup was left behind, so the screen crashed on the first render that reached it. */}
    </PermissionGuard>
  );
};

export default NotificationSchedulesTab;
